import { useId, useState, useEffect } from "react";

import { IconButton, Tooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { useSessionStorage } from "app/pages/activation/hooks/helpers/useSessionStorage";

import * as SC from "./styles";
import { COLLAPSE, EXPAND } from "./texts";
import type { CollapseSchema } from "./type";

export const Collapse = ({
    title,
    defaultOpen = false,
    isOpen,
    className,
    children,
    rightActionBlock,
    contentClassName,
    onToggle,
}: CollapseSchema) => {
    const isControlled = typeof isOpen === "boolean";
    const { getSessionStorage, setSessionStorage } = useSessionStorage();

    const storageKey = `collapse-${title}`;
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
    const open = isControlled ? isOpen : uncontrolledOpen;

    const contentId = useId();
    const headerId = useId();

    useEffect(() => {
        if (!isControlled) {
            const stored = getSessionStorage(storageKey);

            if (stored !== null) {
                setUncontrolledOpen(stored === "true");
            }
        }
    }, [isControlled, storageKey]);

    const handleToggle = () => {
        const next = !open;

        if (!isControlled) {
            setUncontrolledOpen(next);
            setSessionStorage(storageKey, String(next));
        }

        onToggle?.(next);
    };

    return (
        <SC.Container className={className}>
            <SC.Header id={headerId}>
                <SC.Title>{title}</SC.Title>
                <Tooltip
                    enabled
                    content={open ? translate(COLLAPSE) : translate(EXPAND)}
                >
                    <IconButton
                        type="flat"
                        onClick={handleToggle}
                        iconName={open ? "chev-down" : "chev-right"}
                    />
                </Tooltip>
                {rightActionBlock}
            </SC.Header>
            <SC.ContentWrapper
                id={contentId}
                role="region"
                aria-labelledby={headerId}
                data-open={open}
                className={contentClassName}
            >
                <SC.ContentInner data-open={open}>{children}</SC.ContentInner>
            </SC.ContentWrapper>
        </SC.Container>
    );
};
